import { motion } from 'framer-motion';
import { Eye } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useOverlay } from '../../contexts/OverlayContext';
import { typography } from '../../styles/typography';

// Format timestamp to display only time (backend sends date + time)
const formatOrderPlacedAt = (timestamp) => {
  if (!timestamp) return '—';
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

// Format preparation time in minutes to "Xhr Ymin"
const formatPreparationTime = (minutes) => {
  if (minutes == null || minutes === '') return '—';
  const total = Number(minutes);
  if (isNaN(total) || total < 0) return '—';
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (hours === 0) return `${mins} min`;
  if (mins === 0) return `${hours} hr`;
  return `${hours} hr ${mins} min`;
};

// Mock data for development – replace with API data later
const MOCK_ORDERS = [
  {
    id: 'ORD-001',
    orderNo: 'ORD-001',
    platform: 'swiggy',
    orderPlacedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    dishNames: 'Butter Chicken, Naan, Rice',
    quantity: 3,
    preparationTimeMinutes: 75,
    status: 'Preparing',
  },
  {
    id: 'ORD-002',
    orderNo: 'ORD-002',
    platform: 'zomato',
    orderPlacedAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
    dishNames: 'Paneer Tikka, Dal Makhani',
    quantity: 2,
    preparationTimeMinutes: 45,
    status: 'Food Ready',
  },
  {
    id: 'ORD-003',
    orderNo: 'ORD-003',
    platform: 'magic_pin',
    orderPlacedAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    dishNames: 'Biryani, Raita',
    quantity: 1,
    preparationTimeMinutes: 60,
    status: 'Accepted',
  },
  {
    id: 'ORD-004',
    orderNo: 'ORD-004',
    platform: 'zomato',
    orderPlacedAt: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
    dishNames: 'Veg Thali, Lassi',
    quantity: 1,
    preparationTimeMinutes: 30,
    status: 'Cancelled',
  },
  {
    id: 'ORD-005',
    orderNo: 'ORD-005',
    platform: 'swiggy',
    orderPlacedAt: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
    dishNames: 'Masala Dosa, Coffee',
    quantity: 2,
    preparationTimeMinutes: 20,
    status: 'Pending',
  },
  
];

const PLATFORM_OPTIONS = [
  { value: '', label: 'All Platforms' },
  { value: 'swiggy', label: 'Swiggy' },
  { value: 'zomato', label: 'Zomato' },
  { value: 'magic_pin', label: 'Magic Pin' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Accepted', label: 'Accepted' },
  { value: 'Declined', label: 'Declined' },
  { value: 'Cancelled', label: 'Cancelled' },
  { value: 'Preparing', label: 'Preparing' },
  { value: 'Food Ready', label: 'Food Ready' },
  { value: 'Dispatched', label: 'Dispatched' },
  { value: 'Delivered', label: 'Delivered' },
];

const getPlatformLabel = (value) =>
  PLATFORM_OPTIONS.find((o) => o.value === value)?.label || value || '—';

// Status badge colors (pill style: white text on colored background)
const STATUS_COLORS = {
  Pending: { bg: '#6C757D', text: '#ffffff' },
  Accepted: { bg: '#28A745', text: '#ffffff' },
  Declined: { bg: '#DC3545', text: '#ffffff' },
  Cancelled: { bg: '#DC3545', text: '#ffffff' },
  Preparing: { bg: '#FD7E14', text: '#ffffff' },
  'Food Ready': { bg: '#0D6EFD', text: '#ffffff' },
  Dispatched: { bg: '#6F42C1', text: '#ffffff' },
  Delivered: { bg: '#198754', text: '#ffffff' },
};

const StatusBadge = ({ status }) => {
  if (!status) return <span className="text-gray-500">—</span>;
  const bg = STATUS_COLORS[status]?.bg ?? '#6C757D';
  return (
    <span
      className="inline-flex items-center justify-center rounded-full px-3 py-1.5 text-sm font-medium"
      style={{ backgroundColor: bg, color: '#ffffff' }}
    >
      {status}
    </span>
  );
};

const LiveOrderFeedScreen = () => {
  const [orders, setOrders] = useState(MOCK_ORDERS);
  const [searchQuery, setSearchQuery] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Use current dropdown values for filtering (live) so table always matches what user sees
  const filteredOrders = useMemo(() => {
    const search = searchQuery.trim();
    const filtered = orders.filter((order) => {
      const matchesSearch =
        !search ||
        order.orderNo?.toLowerCase().includes(search.toLowerCase()) ||
        order.dishNames?.toLowerCase().includes(search.toLowerCase());
      const matchesPlatform = !platformFilter || order.platform === platformFilter;
      const matchesStatus = !statusFilter || order.status === statusFilter;
      return matchesSearch && matchesPlatform && matchesStatus;
    });
    // Latest order on top (sort by orderPlacedAt descending)
    return [...filtered].sort((a, b) => {
      const tA = a.orderPlacedAt ? new Date(a.orderPlacedAt).getTime() : 0;
      const tB = b.orderPlacedAt ? new Date(b.orderPlacedAt).getTime() : 0;
      return tB - tA;
    });
  }, [orders, searchQuery, platformFilter, statusFilter]);

  const { openOverlay } = useOverlay();
  const handleViewOrder = (order) => {
    openOverlay('liveOrderDetails', {
      order,
      onAccept: (acceptedOrder) => {
        setOrders((prev) =>
          prev.map((o) => (o.id === acceptedOrder.id ? { ...o, status: 'Accepted' } : o))
        );
      },
      onDecline: (declinedOrder) => {
        setOrders((prev) =>
          prev.map((o) => (o.id === declinedOrder.id ? { ...o, status: 'Declined' } : o))
        );
      },
    });
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <div className="w-full min-h-screen">
      <div className="mx-auto h-auto">
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          {/* Header */}
          {/* <div className="mb-8">
            <h1 className={typography.h2} style={{ color: '#000000' }}>
              Live Order Feed
            </h1>
            <p className="mt-2 text-gray-600">
              Search and filter orders by platform and status
            </p>
          </div> */}

          {/* Filters: Search, Platform, Status (live filtering) */}
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex flex-wrap items-end gap-4">
              {/* Search */}
              <div className="flex-1 min-w-[200px]">
                <label
                  htmlFor="live-order-search"
                  className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700"
                >
                  Search
                </label>
                <input
                  id="live-order-search"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Order no., dish names..."
                  className="w-full min-h-[48px] rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                />
              </div>

              {/* Platform dropdown */}
              <div className="min-w-[180px]">
                <label
                  htmlFor="live-order-platform"
                  className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700"
                >
                  Platform
                </label>
                <select
                  id="live-order-platform"
                  value={platformFilter}
                  onChange={(e) => setPlatformFilter(e.target.value)}
                  className="w-full min-h-[48px] rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                >
                  {PLATFORM_OPTIONS.map((opt) => (
                    <option key={opt.value || 'all'} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status dropdown */}
              <div className="min-w-[180px]">
                <label
                  htmlFor="live-order-status"
                  className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700"
                >
                  Status
                </label>
                <select
                  id="live-order-status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full min-h-[48px] rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value || 'all'} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

            </div>
          </div>

          {/* Table */}
          <div className="rounded-3xl">
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
              <table className="min-w-full border-collapse text-sm md:text-base">
                <thead>
                  <tr className="bg-gray-50 text-left text-gray-600">
                    <th className="px-4 py-3 font-semibold min-w-[100px]">
                      Order no.
                    </th>
                    <th className="px-4 py-3 font-semibold min-w-[120px]">
                      Platform
                    </th>
                    <th className="px-4 py-3 font-semibold min-w-[160px]">
                      Order placed at
                    </th>
                    <th className="px-4 py-3 font-semibold min-w-[200px]">
                      Dish names
                    </th>
                    <th className="px-4 py-3 font-semibold min-w-[90px]">
                      Quantity
                    </th>
                    <th className="px-4 py-3 font-semibold min-w-[140px]">
                      Preparation time
                    </th>
                    <th className="px-4 py-3 font-semibold min-w-[120px]">
                      Status
                    </th>
                    <th className="px-4 py-3 font-semibold min-w-[120px]">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.length === 0 ? (
                    <tr>
                      <td
                        colSpan={8}
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        No orders match the current filters.
                      </td>
                    </tr>
                  ) : (
                    filteredOrders.map((order) => (
                      <tr
                        key={order.id}
                        className="border-t border-gray-100 hover:bg-gray-50/50"
                      >
                        <td className="px-4 py-4 text-gray-800">
                          {order.orderNo || '—'}
                        </td>
                        <td className="px-4 py-4 text-gray-800">
                          {getPlatformLabel(order.platform)}
                        </td>
                        <td className="px-4 py-4 text-gray-800">
                          {formatOrderPlacedAt(order.orderPlacedAt)}
                        </td>
                        <td className="px-4 py-4 text-gray-800">
                          {order.dishNames || '—'}
                        </td>
                        <td className="px-4 py-4 text-gray-800">
                          {order.quantity ?? '—'}
                        </td>
                        <td className="px-4 py-4 text-gray-800">
                          {formatPreparationTime(order.preparationTimeMinutes)}
                        </td>
                        <td className="px-4 py-4">
                          <StatusBadge status={order.status} />
                        </td>
                        <td className="px-4 py-4">
                          <button
                            type="button"
                            onClick={() => handleViewOrder(order)}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-600 text-sm font-semibold hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                            style={{ color: '#ffffff' }}
                          >
                            <Eye className="w-4 h-4" style={{ color: '#ffffff' }} />
                            View order
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LiveOrderFeedScreen;
